import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { HtmlParser } from '@hozana/dom/components/HtmlParser'
import { Translate } from '@hozana/intl/Translate'
import { useScreen } from '@hozana/screen/useScreen'

import { Button } from 'elements/button/Button'
import { AbsoluteDiv } from 'elements/layout/AbsoluteDiv'
import { Div } from 'elements/layout/Div'
import { FixedDiv } from 'elements/layout/FixedDiv'
import { Space } from 'elements/layout/Space'
import { Icon } from 'elements/media/Icon'
import { H } from 'elements/text/H'
import { Link } from 'elements/text/Link'
import { P } from 'elements/text/P'
import { Panel } from 'elements/ui/Panel'

import { ICON } from 'config/icons'
import { PAGE } from 'routes/pages'

import { TPrayerMeeting } from 'modules/meeting/types'

import { TPrayerEvent } from '../../types'
import { PrayerEventInfoCardFooter } from './PrayerEventInfoFooter'

const CardDiv = styled(FixedDiv)`
  box-shadow: 0px 3px 6px #00000029;
`
type TPrayerEventInfoCardProps = {
  prayerEvent: TPrayerEvent
  prayerMeetings: TPrayerMeeting[]
}
export const PrayerEventInfo: TFC<TPrayerEventInfoCardProps> = ({ prayerEvent, prayerMeetings }) => {
  const { t } = useTranslation()
  const screen = useScreen()

  const [viewInfoMode, setViewInfoMode] = useState<'small' | 'medium' | 'panel'>('medium')

  const eventStartDate = dayjs(prayerEvent.startAt)
  const eventEndDate = dayjs(prayerEvent.endAt)
  const isSingleDayEvent =
    dayjs(prayerEvent.endAt).diff(dayjs(prayerEvent.startAt), 'day', true) <= 1 &&
    dayjs(prayerEvent.endAt).day() === dayjs(prayerEvent.startAt).day()

  return (
    <>
      {viewInfoMode === 'medium' && (
        <CardDiv
          zIndex="mobileBanner"
          w={{ xs: '95%', md: '500px' }}
          bottom="1em"
          right="10px"
          left="10px"
          m="auto"
          p="1rem"
          bg="white"
          radius="big"
        >
          <AbsoluteDiv right="10px" top="10px" onClick={() => setViewInfoMode('small')}>
            <Icon name={ICON.CLOSE} color="black50" size="1em" />
          </AbsoluteDiv>
          <Div display="flex">
            <Icon name={ICON.EVENT} color="orange" size="34px" m="0 10px 0 0px" />
            <P fontSize="1em">
              <HtmlParser>
                {t('event:event.map.card-medium.title', {
                  prayerEventTitle: prayerEvent.title,
                  prayerEventStartDate: eventStartDate.format('DD MMM YYYY'),
                  prayerEventEndDate: eventEndDate.format('DD MMM YYYY'),
                  dates: isSingleDayEvent ? 0 : 2,
                })}
              </HtmlParser>
            </P>
          </Div>
          <Space />
          <Div w="100%" align="center">
            <Button
              bg="mediumBlue"
              borderColor="mediumBlue"
              w="160px"
              m="auto"
              onClick={() => setViewInfoMode('panel')}
            >
              trans:event:word.discover
            </Button>
          </Div>
          <Space />
          <PrayerEventInfoCardFooter
            prayerEvent={prayerEvent}
            prayerMeetings={prayerMeetings}
            isSingleDayEvent={isSingleDayEvent}
          />
        </CardDiv>
      )}
      <Panel
        from={screen.xs ? 'bottom' : 'right'}
        open={viewInfoMode === 'panel'}
        size={{ xs: '80%', md: '50%', lg: '30%' }}
        close={() => setViewInfoMode('small')}
      >
        <H size="2" fontSize="1.5em" w="80%">
          <Translate
            values={{
              prayerEventTitle: prayerEvent.title,
              prayerEventStartDate: dayjs(prayerEvent.startAt).format('DD MMMM YYYY'),
              prayerEventEndDate: dayjs(prayerEvent.endAt).format('DD MMMM YYYY'),
              dates: isSingleDayEvent ? 0 : 2,
            }}
          >
            trans:event:event.map.panel.title
          </Translate>
        </H>
        <Space />
        <P fontSize="1em">{prayerEvent.content}</P>
        <Space />
        <Link
          to={{ pathname: PAGE.STATIC, query: { pageSlug: prayerEvent.url.split('/').slice(-1)[0] } }}
          target="_blank"
        >
          <P color="blue" align="center" bold fontSize="1em" underline>
            {t('event:event.map.panel.learn-more')}
          </P>
        </Link>
        <Space />
        <P fontSize="1em" bold align="center">
          {t('event:event.map.panel.organize.description')}
        </P>
        <Space />
        <Button
          valueLink={{
            to: {
              pathname: PAGE.MEETING_CREATE,
              query: {
                prayerEventId: prayerEvent.id,
                prayerEventSlug: prayerEvent.slug,
              },
            },
            target: '_blank',
          }}
          colors="yellow"
          w="100%"
        >
          <P fontSize="1em">trans:event:event.map.panel.organize.organize</P>
        </Button>
        <Space />
      </Panel>
      {viewInfoMode === 'small' && (
        <CardDiv
          zIndex="mobileBanner"
          w={{ xs: '95%', md: '500px' }}
          bottom="1em"
          right="10px"
          left="10px"
          m="auto"
          p="1rem"
          bg="white"
          radius="big"
        >
          <Div display="flex" align="middleBetween">
            <Div display="flex" align="middleCenter">
              <Icon name={ICON.EVENT} color="orange" size="34px" m="0 10px 0 0px" />
              <P fontSize="1em" bold>
                {prayerEvent.title}
              </P>
            </Div>
            <Button bg="mediumBlue" borderColor="mediumBlue" w="124px" onClick={() => setViewInfoMode('panel')}>
              trans:event:word.discover
            </Button>
          </Div>
          <Space />
          <PrayerEventInfoCardFooter
            prayerEvent={prayerEvent}
            prayerMeetings={prayerMeetings}
            isSingleDayEvent={isSingleDayEvent}
          />
        </CardDiv>
      )}
    </>
  )
}
